import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'

type MotionStyle = CSSProperties & {
  '--reveal-delay'?: string
}

let revealObserver: IntersectionObserver | null = null

function getRevealObserver() {
  if (revealObserver) return revealObserver

  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      const element = entry.target as HTMLElement
      element.dataset.revealState = 'visible'
      revealObserver?.unobserve(element)
    })
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 })

  return revealObserver
}

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    document.documentElement.classList.add('motion-ready')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      element.dataset.revealState = 'visible'
      return
    }

    const observer = getRevealObserver()
    observer.observe(element)
    return () => observer.unobserve(element)
  }, [])

  return ref
}

type RevealProps = {
  children: ReactNode
  className?: string
  direction?: 'up' | 'left' | 'right' | 'mask'
  delay?: number
}

export function Reveal({ children, className = '', direction = 'up', delay = 0 }: RevealProps) {
  const ref = useReveal<HTMLDivElement>()
  const style: MotionStyle = { '--reveal-delay': `${delay}ms` }

  return (
    <div
      ref={ref}
      className={`reveal reveal--${direction} ${className}`.trim()}
      data-reveal-state="hidden"
      style={style}
    >
      {children}
    </div>
  )
}

type StaggerGroupProps = {
  children: ReactNode
  className?: string
  label?: string
}

export function StaggerGroup({ children, className = '', label }: StaggerGroupProps) {
  const ref = useReveal<HTMLDivElement>()

  return (
    <div
      ref={ref}
      className={`stagger-group ${className}`.trim()}
      data-reveal-state="hidden"
      aria-label={label}
    >
      {children}
    </div>
  )
}

export function Marquee({ items, label }: { items: readonly string[]; label: string }) {
  const sequence = (hidden: boolean) => (
    <div className="marquee__sequence" aria-hidden={hidden || undefined}>
      {items.map((item, index) => (
        <span key={`${item}-${index}`}><span>{item}</span><i aria-hidden="true">•</i></span>
      ))}
    </div>
  )

  return (
    <aside className="marquee" aria-label={label}>
      <div className="marquee__track">
        {sequence(false)}
        {sequence(true)}
      </div>
    </aside>
  )
}

export function FloatingDecorations() {
  const layerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const layer = layerRef.current
    const host = layer?.closest<HTMLElement>('.hero')
    if (!layer || !host) return

    const finePointer = window.matchMedia('(pointer: fine)')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (!finePointer.matches || reducedMotion.matches) return

    let frame = 0
    const update = (event: PointerEvent) => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        const bounds = host.getBoundingClientRect()
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2
        layer.style.setProperty('--parallax-near-x', `${(x * 8).toFixed(2)}px`)
        layer.style.setProperty('--parallax-near-y', `${(y * 8).toFixed(2)}px`)
        layer.style.setProperty('--parallax-mid-x', `${(x * 15).toFixed(2)}px`)
        layer.style.setProperty('--parallax-mid-y', `${(y * 15).toFixed(2)}px`)
        layer.style.setProperty('--parallax-far-x', `${(x * 22).toFixed(2)}px`)
        layer.style.setProperty('--parallax-far-y', `${(y * 22).toFixed(2)}px`)
      })
    }
    const reset = () => {
      ;['near', 'mid', 'far'].forEach((depth) => {
        layer.style.setProperty(`--parallax-${depth}-x`, '0px')
        layer.style.setProperty(`--parallax-${depth}-y`, '0px')
      })
    }

    host.addEventListener('pointermove', update, { passive: true })
    host.addEventListener('pointerleave', reset, { passive: true })
    return () => {
      window.cancelAnimationFrame(frame)
      host.removeEventListener('pointermove', update)
      host.removeEventListener('pointerleave', reset)
    }
  }, [])

  return (
    <div className="hero-decorations" ref={layerRef} aria-hidden="true">
      <span className="floating-decoration floating-decoration--code">
        <svg viewBox="0 0 80 80"><path d="m31 24-15 16 15 16M49 24l15 16-15 16M46 17 34 63" /></svg>
      </span>
      <span className="floating-decoration floating-decoration--ai">
        <svg viewBox="0 0 80 80"><circle cx="19" cy="40" r="4" /><circle cx="40" cy="20" r="4" /><circle cx="61" cy="40" r="4" /><circle cx="40" cy="60" r="4" /><path d="m22 37 15-14m6 0 15 14m0 6L43 57m-6 0L22 43m17-19v32M23 40h34" /></svg>
      </span>
      <span className="floating-decoration floating-decoration--video">
        <svg viewBox="0 0 80 80"><rect x="13" y="18" width="54" height="44" rx="8" /><path d="m35 31 16 9-16 9Z" /></svg>
      </span>
      <span className="floating-decoration floating-decoration--audio">
        <svg viewBox="0 0 96 48"><path d="M4 24h8l5-13 8 28 8-35 8 40 8-30 8 21 7-17 7 11h21" /></svg>
      </span>
      <span className="floating-decoration floating-decoration--mobile">
        <svg viewBox="0 0 80 80"><rect x="23" y="10" width="34" height="60" rx="8" /><path d="M34 17h12M36 62h8" /></svg>
      </span>
      <span className="floating-decoration floating-decoration--fm">FM</span>
    </div>
  )
}
